import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "neuromath_access_token";

/**
 * Guarda/lee/elimina el access token desde localStorage.
 * Se usa como **fallback** cuando las cookies httpOnly son descartadas
 * por el navegador (caso típico: Safari iOS/macOS con ITP bloqueando
 * cookies SameSite=None entre dominios productivos).
 */
export function setAccessToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch (_) { /* modo privado de Safari puede tirar QuotaExceeded; ignorar */ }
}
export function getAccessToken() {
  try { return localStorage.getItem(TOKEN_KEY) || ""; } catch (_) { return ""; }
}
export function clearAccessToken() {
  try { localStorage.removeItem(TOKEN_KEY); } catch (_) { /* ignore */ }
}

export const api = axios.create({
  baseURL: API,
  timeout: 20000,
  withCredentials: true,
});

// Interceptor: si tenemos token en localStorage, lo enviamos como Bearer.
// El backend ya soporta este header como alternativa a las cookies.
api.interceptors.request.use((config) => {
  const t = getAccessToken();
  if (t) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

// Convenient helper for rendering FastAPI 422 errors as a string.
export function formatApiErrorDetail(detail) {
  if (detail == null) return "Algo salió mal. Inténtalo de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : (e && typeof e.message === "string" ? e.message : JSON.stringify(e)))).filter(Boolean).join(" ");
  if (detail && typeof detail.message === "string") return detail.message;
  if (detail && typeof detail.msg === "string") return detail.msg;
  return "Algo salió mal. Inténtalo de nuevo.";
}


// ─────────────────────────────────────────────────────────────────────────────
// v5.7.4 · Sesión robusta
//  1) Un error 401 ya no deja la pantalla rota: se renueva la sesión UNA vez (/auth/refresh) y se repite
//     la petición. Solo si la renovación también devuelve 401 se avisa de que la sesión terminó.
//  2) Las lecturas (GET) que fallan por red o por el servidor ocupado (502/503/504/52x) se reintentan 2 veces.
//     Antes un fallo momentáneo equivalía a «error al ingresar» o a quedar fuera de la plataforma.
// ─────────────────────────────────────────────────────────────────────────────
let refreshing = null;
export function renewSession() {
  if (!refreshing) {
    refreshing = api.post("/auth/refresh", null, { _skipAuthRetry: true })
      .then(({ data }) => { if (data?.access_token) setAccessToken(data.access_token); return true; })
      .finally(() => { setTimeout(() => { refreshing = null; }, 0); });
  }
  return refreshing;
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const isTransient = (err) => {
  const st = err?.response?.status;
  return !err?.response || st === 502 || st === 503 || st === 504 || (st >= 520 && st <= 524);
};

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const cfg = error?.config;
    if (!cfg) return Promise.reject(error);
    const url = String(cfg.url || "");
    const isAuthCall = /\/auth\/(login|refresh|logout|forgot|reset|recover)/.test(url);

    // 1) Sesión vencida → renovar y repetir una vez
    if (error.response?.status === 401 && !cfg._skipAuthRetry && !cfg._retried401 && !isAuthCall) {
      cfg._retried401 = true;
      try {
        await renewSession();
        cfg.headers = { ...(cfg.headers || {}) };
        delete cfg.headers.Authorization; // el interceptor de petición pondrá el token nuevo
        return api(cfg);
      } catch (e2) {
        if (e2?.response?.status === 401) {
          try { window.dispatchEvent(new CustomEvent("jasq:session-expired")); } catch (_) { /* sin window */ }
        }
        return Promise.reject(error);
      }
    }

    // 2) Fallos transitorios en lecturas → reintentar con espera
    const method = String(cfg.method || "get").toLowerCase();
    if (method === "get" && isTransient(error) && !axios.isCancel(error)) {
      cfg._retryN = (cfg._retryN || 0) + 1;
      if (cfg._retryN <= 2) {
        await sleep(cfg._retryN === 1 ? 900 : 2200);
        return api(cfg);
      }
    }
    return Promise.reject(error);
  },
);
